import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    Chip,
    Tooltip,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Info as InfoIcon,
    Cancel as CancelIcon,
    Check as CheckIcon,
    Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, ProjectSimulation, ProjectPosition } from '../../types/project';
import { projectService } from '../../services/projectService';
import { formatCurrency } from '../../utils/format';
import { formatDateBR } from '../../utils/date';
import { CLTCostDetails } from '../common/CLTCostDetails';
import { SCPCostDetails } from '../common/SCPCostDetails';
import { PJCostDetails } from '../common/PJCostDetails';
import { useCLTCalculator } from '../../hooks/useCLTCalculator';
import ShareProposalDialog from './ShareProposalDialog';
import ProposalSectionsList from '../proposal/ProposalSectionsList';
import { ProposalSection } from '../../types/proposal';
import * as proposalService from '../../services/proposalService';

interface ProjectProposalDetailsProps {
    project: Project;
    onBack: () => void;
    onReload: () => void;
}

const ProjectProposalDetails: React.FC<ProjectProposalDetailsProps> = ({
    project,
    onBack,
    onReload,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [selectedCLTPosition, setSelectedCLTPosition] = useState<any>(null);
    const [selectedSCPPosition, setSelectedSCPPosition] = useState<any>(null);
    const [selectedPJPosition, setSelectedPJPosition] = useState<any>(null);
    const cltCalculator = useCLTCalculator();
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({ open: false, message: '', severity: 'success' });
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    // Remover toda a lógica relacionada a seções da proposta
    // Remover o botão "Adicionar Nova Seção" e qualquer formulário ou handler relacionado

    // Constantes para os índices de encargos
    const ENCARGOS = {
        CLT: 1.00, // Será calculado pelo CLTCalculator
        PJ: 1.15,
        SCP: 1.30
    };

    const calculatePositionCost = (position: ProjectPosition): number => {
        const baseSalary = position.base_salary || 0;
        const allocation = position.allocation || 0;
        
        if (baseSalary === 0) return 0;
        
        if (position.contract_type === 'CLT') {
            const cltCost = cltCalculator.calculateCosts(baseSalary);
            return cltCost.custoTotal * (allocation / 100);
        }
        if (position.contract_type === 'SCP') {
            const scpCost = cltCalculator.calculateSCPCosts(baseSalary);
            return scpCost.custoTotal * (allocation / 100);
        }
        if (position.contract_type === 'PJ') {
            const pjCost = cltCalculator.calculatePJCosts(baseSalary);
            return pjCost.custoTotal * (allocation / 100);
        }
        const encargo = ENCARGOS[position.contract_type as keyof typeof ENCARGOS] || 1;
        return baseSalary * (allocation / 100) * encargo;
    };

    // Data pura da API ("2026-10-01") lida como local; antes aparecia um dia antes (fuso UTC-3)
    const formatDate = (dateString: string | null | undefined) => formatDateBR(dateString);

    const handleRejectProposal = async () => {
        if (!rejectionReason.trim() || !project.selectedSimulationId) return;

        try {
            await projectService.rejectProjectSimulation(
                project.id,
                project.selectedSimulationId,
                rejectionReason
            );
            setRejectDialogOpen(false);
            onReload();
        } catch (error) {
            console.error('Error rejecting proposal:', error);
        }
    };

    const handleShowCLTDetails = (position: ProjectPosition) => {
        setSelectedCLTPosition(position);
    };

    const handleShowSCPDetails = (position: ProjectPosition) => {
        setSelectedSCPPosition(position);
    };

    const handleShowPJDetails = (position: ProjectPosition) => {
        setSelectedPJPosition(position);
    };

    const handleAcceptProposal = async () => {
        try {
            setAccepting(true);
            await projectService.acceptProjectProposal(project.id);
            setAcceptDialogOpen(false);
            setSnackbar({
                open: true,
                message: 'Proposta aceita com sucesso!',
                severity: 'success'
            });
            onReload();
        } catch (error) {
            console.error('Error accepting proposal:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao aceitar proposta',
                severity: 'error'
            });
        } finally {
            setAccepting(false);
        }
    };

    const handleShareProposal = () => {
        setShareDialogOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (!project.simulation) {
        return <Typography>Nenhuma simulação selecionada</Typography>;
    }

    return (
        <Box sx={{ p: { xs: 0, md: 3 } }}>
            {/* flexWrap: no celular os botões de ação ficavam fora da tela */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2} minWidth={0}>
                    <IconButton onClick={onBack} color="primary" aria-label="Voltar">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' }, overflowWrap: 'anywhere' }}>
                        {project.name}
                    </Typography>
                </Box>
                <Box display="flex" gap={1.5} flexWrap="wrap">
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => setAcceptDialogOpen(true)}
                        startIcon={<CheckIcon />}
                    >
                        Aceitar Proposta
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleShareProposal}
                        startIcon={<ShareIcon />}
                    >
                        Compartilhar Link
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setRejectDialogOpen(true)}
                        startIcon={<CancelIcon />}
                    >
                        Rejeitar
                    </Button>
                </Box>
            </Box>

            {/* Informações Gerais */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informações do Projeto
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography>
                                    <strong>Cliente:</strong> {project.client?.name}
                                </Typography>
                                <Typography>
                                    <strong>Status:</strong>{' '}
                                    <Chip
                                        label={project.statusLabel}
                                        color="primary"
                                        size="small"
                                    />
                                </Typography>
                                <Typography>
                                    <strong>Início:</strong> {formatDate(project.simulation.start_date)}
                                </Typography>
                                <Typography>
                                    <strong>Duração:</strong> {project.simulation.duration_months} meses
                                </Typography>
                                <Typography>
                                    <strong>Fim:</strong> {formatDate(project.simulation.end_date)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Resumo Financeiro
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography>
                                    <strong>Receita Total:</strong> {formatCurrency(project.simulation.total_revenue)}
                                </Typography>
                                <Typography>
                                    <strong>Custos Totais:</strong> {formatCurrency(project.simulation.total_costs)}
                                </Typography>
                                <Typography>
                                    <strong>Lucro Total:</strong> {formatCurrency(project.simulation.total_profit)}
                                </Typography>
                                <Typography>
                                    <strong>Margem de Lucro:</strong> {project.simulation.profit_margin.toFixed(2)}%
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Posições */}
            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    Posições
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Cargo</TableCell>
                                <TableCell align="center">Tipo</TableCell>
                                <TableCell align="center">Salário Base</TableCell>
                                <TableCell align="center">Alocação</TableCell>
                                <TableCell align="center">Início</TableCell>
                                <TableCell align="center">Fim</TableCell>
                                <TableCell align="center">Custo mensal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {project.simulation.positions.map((position, index) => (
                                <TableRow key={index}>
                                    <TableCell>{position.role}</TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                            {position.contract_type}
                                            {position.contract_type === 'CLT' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowCLTDetails(position)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {position.contract_type === 'SCP' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowSCPDetails(position)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {position.contract_type === 'PJ' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowPJDetails(position)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{formatCurrency(position.base_salary)}</TableCell>
                                    <TableCell align="center">{position.allocation}%</TableCell>
                                    <TableCell align="center">{formatDate(position.start_date)}</TableCell>
                                    <TableCell align="center">{formatDate(position.end_date)}</TableCell>
                                    <TableCell align="center">{formatCurrency(calculatePositionCost(position))}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Confirmação do aceite: muda o projeto para execução e fixa a simulação (não volta atrás) */}
            <Dialog open={acceptDialogOpen} onClose={() => !accepting && setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Aceitar a proposta?</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        O projeto <b>{project.name}</b> passa para <b>execução</b> com a simulação
                        "{project.simulation?.name}" ({formatCurrency(project.simulation?.total_revenue || 0)} de receita).
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        A partir daí as posições ficam disponíveis para abrir vagas e alocar pessoas, e a proposta
                        não pode mais ser editada nem rejeitada. Confirme apenas após o aceite formal do cliente.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAcceptDialogOpen(false)} disabled={accepting}>Cancelar</Button>
                    <Button variant="contained" color="success" onClick={handleAcceptProposal} disabled={accepting}>
                        Confirmar aceite
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Rejeição */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Rejeitar Proposta</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Por favor, explique o motivo da rejeição da proposta:
                    </Typography>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Digite o motivo da rejeição..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleRejectProposal}
                        color="error"
                        variant="contained"
                        disabled={!rejectionReason.trim()}
                    >
                        Rejeitar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogos de detalhes de custos */}
            {selectedCLTPosition && (
                <CLTCostDetails
                    open={!!selectedCLTPosition}
                    onClose={() => setSelectedCLTPosition(null)}
                    costBreakdown={selectedCLTPosition.costs}
                />
            )}
            {selectedSCPPosition && (
                <SCPCostDetails
                    open={!!selectedSCPPosition}
                    onClose={() => setSelectedSCPPosition(null)}
                    costBreakdown={selectedSCPPosition.costs}
                />
            )}
            {selectedPJPosition && (
                <PJCostDetails
                    open={!!selectedPJPosition}
                    onClose={() => setSelectedPJPosition(null)}
                    costBreakdown={selectedPJPosition.costs}
                />
            )}

            {/* Snackbar para mensagens */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Adicionar o ShareProposalDialog */}
            <ShareProposalDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                project={project}
            />
        </Box>
    );
};

export default ProjectProposalDetails; 